import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, RefreshCw, Plus, Trash2, AlertTriangle, CheckCircle, XCircle,
  Eye, EyeOff, Database, Table2, Users, Terminal, Radio, Rss, Play, Power, Pencil,
} from 'lucide-react';
import { API, apiFetch } from '../apiClient';

type ServerRow = { id: string; label: string; host: string; port: number; username: string };
type ServerStatus = 'unknown' | 'online' | 'offline' | 'testing';

interface DatabasePageProps {
  servers: ServerRow[];
  passwords: Record<string, string>;
  setPassword: (id: string, v: string) => void;
  showPw: Record<string, boolean>;
  setShowPw: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  serverStatus: Record<string, { status: ServerStatus; info: string }>;
  testConnection: (server: ServerRow) => void;
}

type Tab = 'databases' | 'roles' | 'tables' | 'sql' | 'publications' | 'subscriptions';

interface DbRow {
  name: string; owner: string; encoding: string; collate: string; ctype: string;
  is_template: boolean; allow_conn: boolean; size: string | null; size_bytes: number | null;
}
interface RoleRow {
  name: string; superuser: boolean; createdb: boolean; createrole: boolean;
  login: boolean; replication: boolean; conn_limit: number; valid_until: string | null;
}
interface TableRow {
  schema: string; name: string; size: string; total_bytes: number;
  est_rows: number; owner: string; partitioned: boolean;
}
interface PubRow {
  name: string; owner: string; all_tables: boolean;
  ins: boolean; upd: boolean; del: boolean; trunc: boolean; tables: string[];
}
interface SubRow {
  name: string; enabled: boolean; conninfo: string; slot_name: string | null;
  publications: string[]; db: string;
}
interface PingInfo {
  running: boolean; version?: string; wal_level?: string; database_count?: string;
  max_replication_slots?: string; max_wal_senders?: string; error?: string;
}

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'databases',     label: 'Databases',     icon: <Database size={14} /> },
  { id: 'roles',         label: 'Roles',         icon: <Users size={14} /> },
  { id: 'tables',        label: 'Tables',        icon: <Table2 size={14} /> },
  { id: 'sql',           label: 'SQL',           icon: <Terminal size={14} /> },
  { id: 'publications',  label: 'Publications',  icon: <Radio size={14} /> },
  { id: 'subscriptions', label: 'Subscriptions', icon: <Rss size={14} /> },
];

export default function DatabasePage({
  servers, passwords, setPassword, showPw, setShowPw, serverStatus, testConnection,
}: DatabasePageProps) {
  const [serverId, setServerId] = useState(servers[0]?.id || '');
  const [tab, setTab] = useState<Tab>('databases');
  const [ping, setPing] = useState<PingInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // data
  const [databases, setDatabases] = useState<DbRow[]>([]);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [tables, setTables] = useState<TableRow[]>([]);
  const [publications, setPublications] = useState<PubRow[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubRow[]>([]);

  // selected working database (used by tables / publications / subscriptions)
  const [workDb, setWorkDb] = useState('postgres');

  // create-form state
  const [newDb, setNewDb] = useState({ name: '', owner: '' });
  const [newRole, setNewRole] = useState({
    name: '', password: '',
    login: true, superuser: false, createdb: false, createrole: false, replication: false,
  });
  const [editRole, setEditRole] = useState<string | null>(null);
  const [editRolePw, setEditRolePw] = useState('');
  const [newPub, setNewPub] = useState({ name: '', allTables: true, tables: '' });
  const [newSub, setNewSub] = useState({
    name: '', connInfo: '', publications: '',
    copyData: true, enabled: true, createSlot: true, slotName: '',
  });

  // sql console
  const [sqlText, setSqlText] = useState('SELECT datname FROM pg_database WHERE datistemplate = false;');
  const [sqlOut, setSqlOut] = useState<{ ok: boolean; text: string } | null>(null);

  const hasPw = !!passwords[serverId]?.trim();

  const pwPayload = () => Object.fromEntries(Object.entries(passwords).filter(([, v]) => v.trim()));

  const post = useCallback(async (path: string, body: object = {}) => {
    const r = await apiFetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ passwords: pwPayload(), ...body }),
    });
    return r.json();
  }, [passwords]);

  const resetBanners = () => { setErr(null); setMsg(null); };

  // ── Loaders ───────────────────────────────────────────────────────────────
  const loadPing = useCallback(async () => {
    if (!serverId || !hasPw) { setPing(null); return; }
    try {
      const r = await post(`/api/pg/${serverId}/ping`, {});
      if (r.error) { setPing({ running: false, error: r.error }); return; }
      setPing(r);
    } catch (e: any) { setPing({ running: false, error: e?.message || String(e) }); }
  }, [serverId, hasPw, post]);

  const loadTab = useCallback(async (which: Tab) => {
    if (!serverId || !hasPw) return;
    setLoading(true); resetBanners();
    try {
      if (which === 'databases') {
        const r = await post(`/api/pg/${serverId}/databases`, {});
        if (r.error) setErr(r.error); else setDatabases(r.databases || []);
      } else if (which === 'roles') {
        const r = await post(`/api/pg/${serverId}/roles`, {});
        if (r.error) setErr(r.error); else setRoles(r.roles || []);
      } else if (which === 'tables') {
        const r = await post(`/api/pg/${serverId}/tables`, { db: workDb });
        if (r.error) setErr(r.error); else setTables(r.tables || []);
      } else if (which === 'publications') {
        const r = await post(`/api/pg/${serverId}/publications`, { db: workDb });
        if (r.error) setErr(r.error); else setPublications(r.publications || []);
      } else if (which === 'subscriptions') {
        const r = await post(`/api/pg/${serverId}/subscriptions`, {});
        if (r.error) setErr(r.error); else setSubscriptions(r.subscriptions || []);
      }
    } catch (e: any) { setErr(e?.message || String(e)); }
    finally { setLoading(false); }
  }, [serverId, hasPw, post, workDb]);

  useEffect(() => { loadPing(); }, [loadPing]);
  useEffect(() => {
    if (hasPw && serverId && tab !== 'sql') loadTab(tab);
  }, [tab, serverId, hasPw, workDb]); // eslint-disable-line

  // database name options for selectors
  const dbOptions = databases.filter((d) => d.allow_conn).map((d) => d.name);
  useEffect(() => {
    if (databases.length && !dbOptions.includes(workDb)) {
      setWorkDb(dbOptions.includes('postgres') ? 'postgres' : (dbOptions[0] || 'postgres'));
    }
  }, [databases]); // eslint-disable-line

  const runAction = async (fn: () => Promise<any>, okMsg: string) => {
    setBusy(true); resetBanners();
    try {
      const r = await fn();
      if (r.error) { setErr(r.error); return false; }
      if (r.success === false) { setErr(r.output || 'Command failed'); return false; }
      setMsg(r.output ? `${okMsg}\n${r.output}` : okMsg);
      return true;
    } catch (e: any) { setErr(e?.message || String(e)); return false; }
    finally { setBusy(false); }
  };

  // ── Databases actions ───────────────────────────────────────────────────────
  const createDatabase = async () => {
    if (!newDb.name.trim()) return;
    const ok = await runAction(
      () => post(`/api/pg/${serverId}/databases/create`, { name: newDb.name.trim(), owner: newDb.owner.trim() || undefined }),
      `Database "${newDb.name.trim()}" created.`);
    if (ok) { setNewDb({ name: '', owner: '' }); loadTab('databases'); loadPing(); }
  };
  const dropDatabase = async (name: string) => {
    const force = window.confirm(`Drop database "${name}"?\n\nClick OK to force-disconnect active sessions (WITH FORCE), or Cancel to abort.`);
    if (!force) return;
    const ok = await runAction(
      () => post(`/api/pg/${serverId}/databases/drop`, { name, force: true }),
      `Database "${name}" dropped.`);
    if (ok) { loadTab('databases'); loadPing(); }
  };

  // ── Roles actions ─────────────────────────────────────────────────────────
  const createRole = async () => {
    if (!newRole.name.trim()) return;
    const ok = await runAction(() => post(`/api/pg/${serverId}/roles/create`, {
      name: newRole.name.trim(),
      password: newRole.password || undefined,
      options: { login: newRole.login, superuser: newRole.superuser, createdb: newRole.createdb, createrole: newRole.createrole, replication: newRole.replication },
    }), `Role "${newRole.name.trim()}" created.`);
    if (ok) { setNewRole({ name: '', password: '', login: true, superuser: false, createdb: false, createrole: false, replication: false }); loadTab('roles'); }
  };
  const alterRolePassword = async (name: string) => {
    if (!editRolePw) return;
    const ok = await runAction(() => post(`/api/pg/${serverId}/roles/alter`, { name, password: editRolePw }), `Password updated for "${name}".`);
    if (ok) { setEditRole(null); setEditRolePw(''); }
  };
  const dropRole = async (name: string) => {
    if (!window.confirm(`Drop role "${name}"? This cannot be undone.`)) return;
    const ok = await runAction(() => post(`/api/pg/${serverId}/roles/drop`, { name }), `Role "${name}" dropped.`);
    if (ok) loadTab('roles');
  };

  // ── SQL console ─────────────────────────────────────────────────────────────
  const runSql = async () => {
    if (!sqlText.trim()) return;
    setBusy(true); setSqlOut(null); resetBanners();
    try {
      const r = await post(`/api/pg/${serverId}/query`, { db: workDb, sql: sqlText });
      if (r.error) { setSqlOut({ ok: false, text: r.error }); return; }
      setSqlOut({ ok: r.success, text: r.output || (r.success ? '(no output)' : 'Query failed') });
    } catch (e: any) { setSqlOut({ ok: false, text: e?.message || String(e) }); }
    finally { setBusy(false); }
  };

  // ── Publications actions ──────────────────────────────────────────────────
  const createPublication = async () => {
    if (!newPub.name.trim()) return;
    const tablesList = newPub.tables.split(/[\n,]+/).map((t) => t.trim()).filter(Boolean);
    const ok = await runAction(() => post(`/api/pg/${serverId}/publications/create`, {
      db: workDb, name: newPub.name.trim(), allTables: newPub.allTables, tables: tablesList,
    }), `Publication "${newPub.name.trim()}" created on ${workDb}.`);
    if (ok) { setNewPub({ name: '', allTables: true, tables: '' }); loadTab('publications'); }
  };
  const dropPublication = async (name: string) => {
    if (!window.confirm(`Drop publication "${name}" on database "${workDb}"?`)) return;
    const ok = await runAction(() => post(`/api/pg/${serverId}/publications/drop`, { db: workDb, name }), `Publication "${name}" dropped.`);
    if (ok) loadTab('publications');
  };

  // ── Subscriptions actions ─────────────────────────────────────────────────
  const createSubscription = async () => {
    if (!newSub.name.trim() || !newSub.connInfo.trim()) return;
    const pubs = newSub.publications.split(/[\n,]+/).map((p) => p.trim()).filter(Boolean);
    const ok = await runAction(() => post(`/api/pg/${serverId}/subscriptions/create`, {
      db: workDb, name: newSub.name.trim(), connInfo: newSub.connInfo.trim(), publications: pubs,
      options: { copyData: newSub.copyData, enabled: newSub.enabled, createSlot: newSub.createSlot, slotName: newSub.slotName.trim() || undefined },
    }), `Subscription "${newSub.name.trim()}" created on ${workDb}.`);
    if (ok) { setNewSub({ name: '', connInfo: '', publications: '', copyData: true, enabled: true, createSlot: true, slotName: '' }); loadTab('subscriptions'); }
  };
  const subAction = async (sub: SubRow, action: 'enable' | 'disable' | 'refresh') => {
    const ok = await runAction(() => post(`/api/pg/${serverId}/subscriptions/action`, { db: sub.db, name: sub.name, action }),
      `Subscription "${sub.name}" ${action === 'refresh' ? 'refreshed' : action + 'd'}.`);
    if (ok) loadTab('subscriptions');
  };
  const dropSubscription = async (sub: SubRow) => {
    const keep = window.confirm(`Drop subscription "${sub.name}"?\n\nClick OK to also detach the remote replication slot first (use this if the source/publisher is unreachable). Cancel to abort.`);
    if (!keep) return;
    const ok = await runAction(() => post(`/api/pg/${serverId}/subscriptions/drop`, { db: sub.db, name: sub.name, dropSlot: false }),
      `Subscription "${sub.name}" dropped.`);
    if (ok) loadTab('subscriptions');
  };

  const sv = servers.find((s) => s.id === serverId);
  const st = serverId ? serverStatus[serverId] : undefined;

  return (
    <div className="hub-outlet dns-hub-outlet">
      <div className="dns-shell">
        <div className="hub-page-head">
          <h1 className="hub-page-h1">Databases / PostgreSQL</h1>
          <p className="hub-page-sub">
            Manage databases, roles, tables, publications and subscriptions via <code>psql</code> over SSH
          </p>
        </div>

        {/* Toolbar */}
        <div className="dns-toolbar">
          <div className="dns-toolbar-left">
            <label className="dns-field">
              <span>Server</span>
              <select className="commit-select dns-server-select" value={serverId} onChange={(e) => { setServerId(e.target.value); setPing(null); }}>
                {servers.length === 0 && <option value="">No servers</option>}
                {servers.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </label>
            {sv && (
              <label className="dns-field dns-pw-field">
                <span>SSH password</span>
                <div className="dns-pw-wrap">
                  <input
                    type={showPw[serverId] ? 'text' : 'password'}
                    className="cfg-input"
                    placeholder="Password"
                    value={passwords[serverId] || ''}
                    onChange={(e) => setPassword(serverId, e.target.value)}
                    autoComplete="off"
                  />
                  <button type="button" className="btn btn-ghost btn-xs" onClick={() => setShowPw((p) => ({ ...p, [serverId]: !p[serverId] }))}>
                    {showPw[serverId] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </label>
            )}
          </div>
          <div className="dns-toolbar-right">
            <button type="button" className="btn btn-ghost btn-sm" disabled={!serverId || !hasPw} onClick={() => sv && testConnection(sv)}>
              Test SSH
            </button>
            {st?.status === 'online' && <span className="dns-status ok">Online</span>}
            {st?.status === 'offline' && <span className="dns-status bad">Offline</span>}
            {st?.status === 'testing' && <span className="dns-status"><Loader2 size={12} className="spin" /></span>}
            <button type="button" className="btn btn-ghost btn-sm" disabled={loading || !serverId || !hasPw} onClick={() => { loadPing(); if (tab !== 'sql') loadTab(tab); }}>
              <RefreshCw size={12} className={loading ? 'spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {/* Connection / version banner */}
        {hasPw && ping && (
          ping.running ? (
            <div className="db-meta-bar">
              <span className="db-chip ok"><Database size={12} /> PostgreSQL {ping.version}</span>
              <span className="db-chip">wal_level: <strong>{ping.wal_level}</strong></span>
              <span className="db-chip">{ping.database_count} databases</span>
              <span className="db-chip">slots: {ping.max_replication_slots} · senders: {ping.max_wal_senders}</span>
              {ping.wal_level !== 'logical' && (
                <span className="db-chip warn"><AlertTriangle size={12} /> wal_level must be "logical" for publications/subscriptions</span>
              )}
            </div>
          ) : (
            <div className="nginx-msg nginx-msg-err dns-banner"><AlertTriangle size={12} />PostgreSQL not reachable: {ping.error}</div>
          )
        )}
        {!hasPw && serverId && (
          <div className="nginx-msg dns-banner"><AlertTriangle size={12} />Enter the SSH password for this server to manage its databases.</div>
        )}

        {/* Tabs */}
        <div className="dns-tabs dns-tabs-seg db-tabs">
          {TABS.map((t) => (
            <button key={t.id} type="button" className={tab === t.id ? 'active' : ''} onClick={() => { setTab(t.id); resetBanners(); }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Banners */}
        {err && <div className="nginx-msg nginx-msg-err dns-banner"><AlertTriangle size={12} /><pre className="dns-msg-pre">{err}</pre></div>}
        {msg && !err && <div className="nginx-msg nginx-msg-ok dns-banner"><CheckCircle size={12} /><pre className="dns-msg-pre">{msg}</pre></div>}

        {/* Working-database selector (shown on tabs that need a db context) */}
        {(tab === 'tables' || tab === 'sql' || tab === 'publications' || tab === 'subscriptions') && (
          <div className="db-workdb-bar">
            <label className="dns-field">
              <span>Database{tab === 'subscriptions' ? ' (for creating new subscription)' : ''}</span>
              <select className="commit-select" value={workDb} onChange={(e) => setWorkDb(e.target.value)} disabled={!dbOptions.length}>
                {dbOptions.length === 0 && <option value={workDb}>{workDb}</option>}
                {dbOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          </div>
        )}

        <div className="db-body">
          {loading && tab !== 'sql' ? (
            <div className="dns-loading"><Loader2 className="spin" size={22} /></div>
          ) : (
            <>
              {/* ── DATABASES ── */}
              {tab === 'databases' && (
                <>
                  <div className="db-form">
                    <input className="cfg-input" placeholder="new_database_name" value={newDb.name} onChange={(e) => setNewDb((p) => ({ ...p, name: e.target.value }))} />
                    <input className="cfg-input" placeholder="owner (optional)" value={newDb.owner} onChange={(e) => setNewDb((p) => ({ ...p, owner: e.target.value }))} />
                    <button className="btn btn-deploy btn-sm" disabled={busy || !hasPw || !newDb.name.trim()} onClick={createDatabase}>
                      {busy ? <Loader2 size={12} className="spin" /> : <Plus size={12} />} Create database
                    </button>
                  </div>
                  <div className="dns-table-wrap">
                    <table className="dns-table db-table">
                      <thead><tr><th>Name</th><th>Owner</th><th>Encoding</th><th>Size</th><th>Flags</th><th aria-label="Actions" /></tr></thead>
                      <tbody>
                        {databases.map((d) => (
                          <tr key={d.name}>
                            <td><strong>{d.name}</strong></td>
                            <td>{d.owner}</td>
                            <td>{d.encoding}</td>
                            <td>{d.size || '—'}</td>
                            <td className="db-flags">
                              {d.is_template && <span className="db-badge">template</span>}
                              {!d.allow_conn && <span className="db-badge muted">no-conn</span>}
                            </td>
                            <td className="dns-col-act">
                              <button className="btn btn-ghost btn-xs dns-del" title="Drop database" disabled={busy || d.is_template || d.name === 'postgres'} onClick={() => dropDatabase(d.name)}>
                                <Trash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {databases.length === 0 && <tr><td colSpan={6} className="dns-empty">No databases.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* ── ROLES ── */}
              {tab === 'roles' && (
                <>
                  <div className="db-form db-form-wrap">
                    <input className="cfg-input" placeholder="role_name" value={newRole.name} onChange={(e) => setNewRole((p) => ({ ...p, name: e.target.value }))} />
                    <input className="cfg-input" type="password" placeholder="password (optional)" value={newRole.password} onChange={(e) => setNewRole((p) => ({ ...p, password: e.target.value }))} autoComplete="new-password" />
                    <div className="db-checks">
                      {(['login', 'superuser', 'createdb', 'createrole', 'replication'] as const).map((k) => (
                        <label key={k} className="vb-check"><input type="checkbox" checked={newRole[k]} onChange={(e) => setNewRole((p) => ({ ...p, [k]: e.target.checked }))} /> {k}</label>
                      ))}
                    </div>
                    <button className="btn btn-deploy btn-sm" disabled={busy || !hasPw || !newRole.name.trim()} onClick={createRole}>
                      {busy ? <Loader2 size={12} className="spin" /> : <Plus size={12} />} Create role
                    </button>
                  </div>
                  <div className="dns-table-wrap">
                    <table className="dns-table db-table">
                      <thead><tr><th>Role</th><th>Attributes</th><th aria-label="Actions" /></tr></thead>
                      <tbody>
                        {roles.map((r) => (
                          <React.Fragment key={r.name}>
                            <tr>
                              <td><strong>{r.name}</strong></td>
                              <td className="db-flags">
                                {r.superuser && <span className="db-badge super">superuser</span>}
                                {r.login ? <span className="db-badge">login</span> : <span className="db-badge muted">nologin</span>}
                                {r.createdb && <span className="db-badge">createdb</span>}
                                {r.createrole && <span className="db-badge">createrole</span>}
                                {r.replication && <span className="db-badge repl">replication</span>}
                              </td>
                              <td className="dns-col-act">
                                <button className="btn btn-ghost btn-xs" title="Change password" disabled={busy} onClick={() => { setEditRole(editRole === r.name ? null : r.name); setEditRolePw(''); }}><Pencil size={13} /></button>
                                <button className="btn btn-ghost btn-xs dns-del" title="Drop role" disabled={busy} onClick={() => dropRole(r.name)}><Trash2 size={13} /></button>
                              </td>
                            </tr>
                            {editRole === r.name && (
                              <tr><td colSpan={3}>
                                <div className="db-inline-form">
                                  <input className="cfg-input" type="password" placeholder={`New password for ${r.name}`} value={editRolePw} onChange={(e) => setEditRolePw(e.target.value)} autoComplete="new-password" />
                                  <button className="btn btn-deploy btn-sm" disabled={busy || !editRolePw} onClick={() => alterRolePassword(r.name)}>{busy ? <Loader2 size={12} className="spin" /> : <CheckCircle size={12} />} Update</button>
                                  <button className="btn btn-ghost btn-sm" onClick={() => { setEditRole(null); setEditRolePw(''); }}><XCircle size={12} /></button>
                                </div>
                              </td></tr>
                            )}
                          </React.Fragment>
                        ))}
                        {roles.length === 0 && <tr><td colSpan={3} className="dns-empty">No roles.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* ── TABLES ── */}
              {tab === 'tables' && (
                <div className="dns-table-wrap">
                  <table className="dns-table db-table">
                    <thead><tr><th>Schema</th><th>Table</th><th>Size</th><th>Est. rows</th><th>Owner</th></tr></thead>
                    <tbody>
                      {tables.map((t) => (
                        <tr key={`${t.schema}.${t.name}`}>
                          <td>{t.schema}</td>
                          <td><strong>{t.name}</strong>{t.partitioned && <span className="db-badge" style={{ marginLeft: 6 }}>partitioned</span>}</td>
                          <td>{t.size}</td>
                          <td>{Number(t.est_rows).toLocaleString()}</td>
                          <td>{t.owner}</td>
                        </tr>
                      ))}
                      {tables.length === 0 && <tr><td colSpan={5} className="dns-empty">No tables in {workDb}.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── SQL CONSOLE ── */}
              {tab === 'sql' && (
                <div className="db-sql">
                  <textarea className="nginx-textarea db-sql-ta" value={sqlText} onChange={(e) => setSqlText(e.target.value)} spellCheck={false} placeholder="SELECT ..." />
                  <div className="db-sql-actions">
                    <span className="dns-hint">Runs against <code>{workDb}</code> as the <code>postgres</code> superuser (60s statement timeout).</span>
                    <button className="btn btn-deploy btn-sm" disabled={busy || !hasPw || !sqlText.trim()} onClick={runSql}>
                      {busy ? <Loader2 size={12} className="spin" /> : <Play size={12} />} Run
                    </button>
                  </div>
                  {sqlOut && (
                    <pre className={`db-sql-out ${sqlOut.ok ? '' : 'err'}`}>{sqlOut.text}</pre>
                  )}
                </div>
              )}

              {/* ── PUBLICATIONS ── */}
              {tab === 'publications' && (
                <>
                  <div className="db-form db-form-wrap">
                    <input className="cfg-input" placeholder="publication_name" value={newPub.name} onChange={(e) => setNewPub((p) => ({ ...p, name: e.target.value }))} />
                    <label className="vb-check"><input type="checkbox" checked={newPub.allTables} onChange={(e) => setNewPub((p) => ({ ...p, allTables: e.target.checked }))} /> FOR ALL TABLES</label>
                    {!newPub.allTables && (
                      <textarea className="cfg-input db-ta-sm" placeholder="schema.table per line, e.g. public.users" value={newPub.tables} onChange={(e) => setNewPub((p) => ({ ...p, tables: e.target.value }))} rows={2} />
                    )}
                    <button className="btn btn-deploy btn-sm" disabled={busy || !hasPw || !newPub.name.trim()} onClick={createPublication}>
                      {busy ? <Loader2 size={12} className="spin" /> : <Plus size={12} />} Create publication
                    </button>
                  </div>
                  <div className="dns-table-wrap">
                    <table className="dns-table db-table">
                      <thead><tr><th>Name</th><th>Owner</th><th>Scope</th><th>Operations</th><th aria-label="Actions" /></tr></thead>
                      <tbody>
                        {publications.map((p) => (
                          <tr key={p.name}>
                            <td><strong>{p.name}</strong></td>
                            <td>{p.owner}</td>
                            <td>{p.all_tables ? <span className="db-badge">all tables</span> : (p.tables.length ? p.tables.join(', ') : '—')}</td>
                            <td className="db-flags">
                              {p.ins && <span className="db-badge">insert</span>}
                              {p.upd && <span className="db-badge">update</span>}
                              {p.del && <span className="db-badge">delete</span>}
                              {p.trunc && <span className="db-badge">truncate</span>}
                            </td>
                            <td className="dns-col-act">
                              <button className="btn btn-ghost btn-xs dns-del" title="Drop publication" disabled={busy} onClick={() => dropPublication(p.name)}><Trash2 size={13} /></button>
                            </td>
                          </tr>
                        ))}
                        {publications.length === 0 && <tr><td colSpan={5} className="dns-empty">No publications in {workDb}.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* ── SUBSCRIPTIONS ── */}
              {tab === 'subscriptions' && (
                <>
                  <div className="db-form db-form-wrap db-sub-form">
                    <input className="cfg-input" placeholder="subscription_name" value={newSub.name} onChange={(e) => setNewSub((p) => ({ ...p, name: e.target.value }))} />
                    <input className="cfg-input db-conn-input" placeholder="host=1.2.3.4 port=5432 dbname=mydb user=repl password=secret" value={newSub.connInfo} onChange={(e) => setNewSub((p) => ({ ...p, connInfo: e.target.value }))} />
                    <input className="cfg-input" placeholder="publication names (comma separated)" value={newSub.publications} onChange={(e) => setNewSub((p) => ({ ...p, publications: e.target.value }))} />
                    <input className="cfg-input" placeholder="slot_name (optional)" value={newSub.slotName} onChange={(e) => setNewSub((p) => ({ ...p, slotName: e.target.value }))} />
                    <div className="db-checks">
                      <label className="vb-check"><input type="checkbox" checked={newSub.enabled} onChange={(e) => setNewSub((p) => ({ ...p, enabled: e.target.checked }))} /> enabled</label>
                      <label className="vb-check"><input type="checkbox" checked={newSub.copyData} onChange={(e) => setNewSub((p) => ({ ...p, copyData: e.target.checked }))} /> copy_data</label>
                      <label className="vb-check"><input type="checkbox" checked={newSub.createSlot} onChange={(e) => setNewSub((p) => ({ ...p, createSlot: e.target.checked }))} /> create_slot</label>
                    </div>
                    <button className="btn btn-deploy btn-sm" disabled={busy || !hasPw || !newSub.name.trim() || !newSub.connInfo.trim()} onClick={createSubscription}>
                      {busy ? <Loader2 size={12} className="spin" /> : <Plus size={12} />} Create subscription
                    </button>
                  </div>
                  <div className="dns-table-wrap">
                    <table className="dns-table db-table">
                      <thead><tr><th>Name</th><th>Database</th><th>Status</th><th>Publications</th><th>Connection</th><th aria-label="Actions" /></tr></thead>
                      <tbody>
                        {subscriptions.map((s) => (
                          <tr key={s.name}>
                            <td><strong>{s.name}</strong></td>
                            <td>{s.db}</td>
                            <td>{s.enabled ? <span className="db-badge ok">enabled</span> : <span className="db-badge muted">disabled</span>}</td>
                            <td>{s.publications.join(', ')}</td>
                            <td className="db-conn-cell"><code>{s.conninfo}</code></td>
                            <td className="dns-col-act db-sub-acts">
                              {s.enabled
                                ? <button className="btn btn-ghost btn-xs" title="Disable" disabled={busy} onClick={() => subAction(s, 'disable')}><Power size={13} /></button>
                                : <button className="btn btn-ghost btn-xs" title="Enable" disabled={busy} onClick={() => subAction(s, 'enable')}><Power size={13} /></button>}
                              <button className="btn btn-ghost btn-xs" title="Refresh publication" disabled={busy} onClick={() => subAction(s, 'refresh')}><RefreshCw size={13} /></button>
                              <button className="btn btn-ghost btn-xs dns-del" title="Drop subscription" disabled={busy} onClick={() => dropSubscription(s)}><Trash2 size={13} /></button>
                            </td>
                          </tr>
                        ))}
                        {subscriptions.length === 0 && <tr><td colSpan={6} className="dns-empty">No subscriptions.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
